<script setup lang="ts">
import type { CmsElementCrossSelling } from "@shopware-pwa/composables-next";
import { ref, computed, useTemplateRef, onMounted, onUnmounted } from "vue";

const props = defineProps<{
  content: CmsElementCrossSelling;
}>();

const { crossSellingTitle, crossSellings } = props.content.data;

const currentTabIndex = ref<number>(0);
const crossSellCollections = computed(() => {
  return (
    crossSellings?.filter(
      (collection) =>
        !!collection.products.length && collection.crossSelling.position !== 1
    ) || []
  );
});

const productSlider = ref(null);
const productSwiperSlider = useSwiper(productSlider, {
  effect: "slide",
  loop: true,
  grabCursor: true,
  autoplay: {
    delay: 2000,
  },
  slidesPerView: 2,
  initialSlide: 0,
  breakpoints: {
    576: {
      slidesPerView: 1,
    },
    768: {
      slidesPerView: 2,
    },
    1024: {
      slidesPerView: 3,
    },
    1200: {
      slidesPerView: 4,
    },
  },
});

const showSlider = ref(false);
const isMobile = ref(false);

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768;
};

const toggleTab = (index: number) => {
  if (currentTabIndex.value === index) return;
  currentTabIndex.value = index;
};

// Check if we're on mobile
onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile);
});

const showNavigation = computed(() => {
  const swiper = productSwiperSlider.instance.value;
  const productsLength =
    crossSellCollections.value[currentTabIndex.value]?.products.length || 0;

  return swiper?.params?.slidesPerView < productsLength;
});

const isSliderReady = computed(
  () =>
    !isMobile.value &&
    (productSwiperSlider.instance.value?.initialized || false)
);

watch(isSliderReady, () => {
  if (isSliderReady.value) {
    showSlider.value = true;
    setInterval(() => {
      if (!isMobile.value) {
        productSwiperSlider.next();
      }
    }, 5000);
  }
});
</script>

<template>
  <div
    ref="crossSellContainer"
    class="cms-element-cross-selling relative px-4 md:px-0 py-14 md:py-24"
    v-if="crossSellCollections.length > 0"
  >
    <div class="container mx-auto">
      <div
        class="wrap flex items-center justify-between mb-4 md:mb-8"
        :class="'container-' + props.content.blockId"
      >
        <div class="title">
          <h3
            class="text-2xl md:text-3xl lg:text-4xl font-bold"
            v-if="crossSellingTitle"
          >
            {{ crossSellingTitle }}
          </h3>

          <h3 class="text-2xl md:text-3xl lg:text-4xl font-bold" v-else>
            {{ $t("product.crossSellingTitle") }}
          </h3>
        </div>

        <div
          class="space-x-0 md:space-x-4 hidden md:flex items-center justify-center absolute md:relative bottom-[30px] md:bottom-0 w-full md:w-auto flex-col md:flex-row"
        >
          <!-- <div class="see-all">
            <NuxtLink
              :to="'#'"
              class="text-dark-bg font-bold text-sm md:text-base"
              >{{ $t("product.seeAll") }}
            </NuxtLink>
          </div> -->
          <div
            v-if="showNavigation"
            class="nav-wrap space-x-2 md:space-x-4 flex items-center justify-center"
          >
            <button
              @click="productSwiperSlider.prev()"
              class="fas fa-chevron-left seller-left-control bg-medium-grey text-white text-xs md:text-base w-6 h-6 md:w-11 md:h-11 flex items-center justify-center rounded-full focus:outline-none hover:text-white hover:bg-dark-bg transition duration-300"
            ></button>
            <button
              @click="productSwiperSlider.next()"
              class="fas fa-chevron-right seller-right-control bg-medium-grey text-white text-xs md:text-base w-6 h-6 md:w-11 md:h-11 flex items-center justify-center rounded-full focus:outline-none hover:text-white hover:bg-dark-bg transition duration-300"
            ></button>
          </div>
        </div>
      </div>
      <div class="flex gap-10 mb-10 container mx-auto">
        <h4
          v-for="(collection, index) of crossSellCollections"
          :key="index"
          class="transition cursor-pointer text-lg md:text-xl font-bold"
          :class="{
            'border-b-3 border-primary text-primary': currentTabIndex === index,
          }"
          @click="toggleTab(index)"
        >
          {{ collection.crossSelling.translated.name }}
        </h4>
      </div>
    </div>
    <div class="mx-auto relative container">
      <!-- Mobile Skeleton loader -->
      <div
        v-if="!isMobile"
        class="overflow-hidden flex gap-4 md:hidden -mx-[20px] px-[20px]"
      >
        <div
          v-for="i in 10"
          :key="i"
          class="animate-pulse min-w-[75%] md:min-w-[50%] lg:min-w-[32%] xl:min-w-[270px]"
        >
          <div class="bg-gray-200 h-64 rounded-lg mb-4"></div>
          <div class="bg-gray-200 h-4 rounded w-3/4 mb-2"></div>
          <div class="bg-gray-200 h-4 rounded w-1/2"></div>
        </div>
      </div>

      <!-- Desktop Skeleton loader -->
      <div v-if="!showSlider" class="overflow-hidden hidden md:flex gap-4">
        <div
          v-for="i in 10"
          :key="i"
          class="animate-pulse min-w-[75%] md:min-w-[50%] lg:min-w-[32%] xl:min-w-[24%]"
        >
          <div class="bg-gray-200 h-64 rounded-lg mb-4"></div>
          <div class="bg-gray-200 h-4 rounded w-3/4 mb-2"></div>
          <div class="bg-gray-200 h-4 rounded w-1/2"></div>
        </div>
      </div>

      <!-- Mobile scroll view -->
      <div v-if="isMobile" class="overflow-x-auto hide-scrollbar -mx-[20px]">
        <div class="flex px-[20px]">
          <div
            v-for="product in crossSellCollections[currentTabIndex].products"
            :key="product.id"
            class="border-r border-custom-grey pr-4 mr-4 min-w-[75%] w-[75%]"
          >
            <ProductSliderCard
              :product="product"
              class="border-0"
            ></ProductSliderCard>
          </div>
        </div>
      </div>

      <!-- Desktop slider -->
      <div
        v-show="showSlider && !isMobile"
        class="inner-wrap overflow-hidden"
        :class="'space-left-' + props.content.blockId"
      >
        <div class="slider seller-uti-slider w-[calc(100% + 2px)] -mx-[1px]">
          <swiper-container ref="productSlider">
            <swiper-slide
              v-for="product of crossSellCollections[currentTabIndex].products"
              :key="product.id"
            >
              <ProductSliderCard
                :product="product"
                class="px-5 border-0 sm:border-r border-custom-grey"
              ></ProductSliderCard>
            </swiper-slide>
          </swiper-container>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
